export interface FooterLink {
  href?: string;
  text: string;
  analyticsText: string;
  onClick?: () => void;
}

export interface FooterSection {
  title: string;
  links: FooterLink[];
}

export interface AccordionSection {
  id: string;
  title: string;
  titleHref: string;
  links: FooterLink[];
}

// Section heading hrefs are absolute URLs, not derived from the directory's
// companyUrl, so they stay local while the link arrays come from the runtime
// config. Known inconsistency.
export const findCareTitleHref =
  'https://montefioreeinsteinadvancedcare.org/services';
export const locationsTitleHref =
  'https://montefioreeinsteinadvancedcare.org/locations';
export const patientResourcesTitleHref =
  'https://montefioreeinsteinadvancedcare.org/patient-resources';
export const forProfessionalsTitleHref =
  'https://montefioreeinsteinadvancedcare.org/for-professionals';

export interface MEACFooterLinks {
  findCareLinks: FooterLink[];
  locationsLinks: FooterLink[];
  patientResourcesLinks: FooterLink[];
  forProfessionalsLinks: FooterLink[];
  accordionData: AccordionSection[];
}

export const buildMEACFooterLinks = (companyUrl: string): MEACFooterLinks => {
  const findCareLinks: FooterLink[] = [
    {
      href: `${companyUrl}/providers`,
      text: 'Find a Doctor',
      analyticsText: 'Find a Doctor',
    },
    {
      href: `${companyUrl}/services`,
      text: 'All Clinical Services',
      analyticsText: 'All Clinical Services',
    },
    {
      href: 'https://mychart.montefiore.org/mychart/Authentication/Login?',
      text: 'MyChart',
      analyticsText: 'MyChart',
    },
    {
      href: `${companyUrl}/appointments`,
      text: 'Make an Appointment',
      analyticsText: 'Make an Appointment',
    },
  ];

  const locationsLinks: FooterLink[] = [
    {
      href: `${companyUrl}/locations/manhattan`,
      text: 'Manhattan',
      analyticsText: 'Manhattan',
    },
    {
      href: `${companyUrl}/locations/orange-county`,
      text: 'Orange County',
      analyticsText: 'Orange County',
    },
    {
      href: `${companyUrl}/locations/rockland-county`,
      text: 'Rockland County',
      analyticsText: 'Rockland County',
    },
    {
      href: `${companyUrl}/locations/westchester`,
      text: 'Westchester County',
      analyticsText: 'Westchester County',
    },
  ];

  const patientResourcesLinks: FooterLink[] = [
    {
      href: `${companyUrl}/patient-resources/billing-insurance`,
      text: 'Billing and Insurance',
      analyticsText: 'Billing and Insurance',
    },
    {
      href: `${companyUrl}/patient-resources/managing-your-care`,
      text: 'Managing Your Care',
      analyticsText: 'Managing Your Care',
    },
    {
      href: 'https://www.healthwise.net/montefiore/',
      text: 'Patient Health Library',
      analyticsText: 'Patient Health Library',
    },
    {
      href: `${companyUrl}/patient-resources/billing-insurance/pay-your-bill`,
      text: 'Pay Your Bill',
      analyticsText: 'Pay Your Bill',
    },
  ];

  const forProfessionalsLinks: FooterLink[] = [
    {
      href: 'https://montefiore.wd12.myworkdayjobs.com/MEAC',
      text: 'Careers',
      analyticsText: 'Careers',
    },
    {
      href: `${companyUrl}/for-professionals/refer-patient`,
      text: 'Refer a Patient',
      analyticsText: 'Refer a Patient',
    },
  ];

  const accordionData: AccordionSection[] = [
    {
      id: 'find-care',
      title: 'Find Care',
      titleHref: findCareTitleHref,
      links: findCareLinks,
    },
    {
      id: 'locations',
      title: 'Locations',
      titleHref: locationsTitleHref,
      links: locationsLinks,
    },
    {
      id: 'patient-resources',
      title: 'Patient Resources',
      titleHref: patientResourcesTitleHref,
      links: patientResourcesLinks,
    },
    {
      id: 'for-professionals',
      title: 'For Professionals',
      titleHref: forProfessionalsTitleHref,
      links: forProfessionalsLinks,
    },
  ];

  return {
    findCareLinks,
    locationsLinks,
    patientResourcesLinks,
    forProfessionalsLinks,
    accordionData,
  };
};

export const complianceLinks: FooterLink[] = [
  {
    href: 'https://montefioreeinstein.org/',
    text: 'Montefiore Einstein',
    analyticsText: 'Montefiore Einstein',
  },
  {
    href: 'https://content.montefioreeinsteinadvancedcare.org/privacy',
    text: 'Privacy Notice',
    analyticsText: 'Privacy Notice',
  },
  {
    href: 'https://content.montefioreeinsteinadvancedcare.org/privacy-policy-web-mobile-applications',
    text: 'Website Privacy Policy',
    analyticsText: 'Website Privacy Policy',
  },
  {
    onClick: (): void => {
      // This object is placed on the window by the OneTrust script
      // which is included in the meac cookie banner.
      // Make sure this is correct in DIR-2587
      const OneTrust = window.OneTrust as
        | { ToggleInfoDisplay: () => void }
        | undefined;
      if (OneTrust?.ToggleInfoDisplay) {
        OneTrust.ToggleInfoDisplay();
      }
    },
    text: 'Cookie Preferences',
    analyticsText: 'Cookie Preferences',
  },
];

const currentYear = new Date().getFullYear();

export const copyrightText = `© ${currentYear} Montefiore Einstein Advanced Care`;
